/**
 * "Show more" at the end of a sidebar tree level whose parent still has a
 * next page (the backend pages files 200 at a time). A `treeitem` wrapper,
 * because a `role="tree"` may only own tree items, around a real button: Tab
 * reaches it and Enter/Space activate it. It stays out of the tree's arrow-key
 * roving model, which moves over items only.
 *
 * Loading is `aria-busy` + `aria-disabled` with a no-op click, not Chakra's
 * `isLoading`: that sets `disabled`, and disabling the focused button drops
 * keyboard focus to <body> mid-request.
 */

import { Button, Flex } from '@chakra-ui/react';
import type { CSSProperties } from 'react';

import { BowerSpinner } from '../../ui/BowerSpinner';
import { TREE_ROW_HEIGHT_PX } from '../../workspace/layout';
import { TREE_INDENT_PX } from './FolderTreeRow';

interface FolderTreeShowMoreRowProps {
  /** 1-based level of the rows it follows. */
  readonly level: number;
  readonly style: CSSProperties;
  readonly isLoading: boolean;
  /** Gets the button, so the tree can tell whether it still holds focus. */
  readonly onShowMore: (button: HTMLElement) => void;
}

export function FolderTreeShowMoreRow({
  level,
  style,
  isLoading,
  onShowMore,
}: FolderTreeShowMoreRowProps) {
  return (
    <Flex
      role="treeitem"
      aria-level={level}
      style={style}
      align="center"
      h={`${TREE_ROW_HEIGHT_PX}px`}
      pl={`${4 + (level - 1) * TREE_INDENT_PX + 16}px`}
    >
      <Button
        variant="ghost"
        size="xs"
        fontWeight="normal"
        color="text.muted"
        _hover={{ bg: 'sidebar.item.hover', color: 'text.primary' }}
        leftIcon={isLoading ? <BowerSpinner size="xs" tone="muted" label={null} /> : undefined}
        aria-busy={isLoading}
        aria-disabled={isLoading || undefined}
        onClick={isLoading ? undefined : (event) => onShowMore(event.currentTarget)}
      >
        Show more
      </Button>
    </Flex>
  );
}
